import dayjs from 'dayjs'

// Get the start of current week (Monday)
export const getWeekStart = (date = new Date()): Date => {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Adjust for Sunday
  return new Date(d.setDate(diff))
}

// Get week range string for display
export const getWeekRangeString = (weekStart = getWeekStart()): string => {
  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekEnd.getDate() + 6)

  const formatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }

  return `${weekStart.toLocaleDateString('zh-CN', formatOptions)}-${weekEnd.getDate()}日`
}

// Calendar start at 07:00 instead of midnight
export const CALENDAR_START_MINUTE = 7 * 60
export const CALENDAR_END_MINUTE = 19 * 60

// Generate time slots for 15-minute intervals from 07:00 to 24:00 (includes 24:00 for grid height)
export const generateTimeSlots = (() => {
  const slots: { time: string; hour: number; minute: number }[] = []
  for (let m = CALENDAR_START_MINUTE; m <= CALENDAR_END_MINUTE; m += 15) {
    const hour = Math.floor(m / 60)
    const minute = m % 60
    const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
    slots.push({ time: timeString, hour, minute })
  }
  return slots
})()

// Convert time string to minutes from midnight
export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

// Convert minutes from midnight to time string
export const minutesToTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}

// Calculate todo height based on duration
export const calculateTodoHeight = (startTime: string, endTime: string): number => {
  const startMinutes = timeToMinutes(startTime)
  const endMinutes = timeToMinutes(endTime)
  const minutes = endMinutes - startMinutes
  return minutes * (PIXELS_PER_15_MIN / 15)
}

// Calculate todo top position based on start time
export const calculateTodoTop = (startTime: string): number => {
  const minutes = timeToMinutes(startTime)
  const relativeMinutes = Math.max(0, minutes - CALENDAR_START_MINUTE)
  return relativeMinutes * (PIXELS_PER_15_MIN / 15)
}

// Check if current time
export const isCurrentTime = (time: string): boolean => {
  const now = dayjs()
  const currentMinutes = now.hour() * 60 + now.minute()
  return Math.abs(currentMinutes - timeToMinutes(time)) <= 7 // Within 15min slot
}

// Get current time line position
export const getCurrentTimeLinePosition = (): number => {
  const now = dayjs()
  const minutesPrecise = now.hour() * 60 + now.minute() + now.second() / 60
  const relativeMinutes = Math.max(0, minutesPrecise - CALENDAR_START_MINUTE)
  return relativeMinutes * (PIXELS_PER_15_MIN / 15)
}

// Format time for display
export const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':')
  return `${hours}:${minutes}`
}

// Get day name from day of week (1-7, Monday-Sunday)
export const getDayName = (dayOfWeek: number): string => {
  const days = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
  return days[dayOfWeek - 1]
}
// Pixels per 15-minute slot (controls grid/todo heights)
export const PIXELS_PER_15_MIN = 24